import { forwardRef, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { Button, type ButtonProps } from './Button';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Typed primary action button rendered on the right */
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    variant?: ButtonProps['variant'];
  };
  /** Free-form right slot (date picker, filters, ...). Rendered before `action`. */
  rightContent?: ReactNode;
  /**
   * En-tete compact des pages de liste, meme disposition a toutes les
   * largeurs : titre et boutons sur une ligne (les boutons passent a la ligne
   * s'il n'y a vraiment pas la place, l'action principale restant sur la
   * ligne du titre), phrase d'explication sur une seule ligne, tronquee
   * (texte complet en infobulle).
   */
  compact?: boolean;
  className?: string;
}

const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ title, subtitle, action, rightContent, compact = false, className }, ref) => {
    if (compact) {
      return (
        <div ref={ref} className={cn('flex min-w-0 flex-col gap-1', className)}>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <h1 className="text-2xl font-bold leading-10 text-dark-500">{title}</h1>
            {(rightContent || action) && (
              // `flex-1` (base 0) : le bloc reste sur la ligne du titre tant que
              // son plus large bouton tient ; `flex-wrap-reverse` : quand les
              // boutons passent a la ligne, le dernier (l'action principale)
              // reste en haut, a cote du titre.
              <div className="flex flex-1 flex-wrap-reverse items-center justify-end gap-2">
                {rightContent}
                {action && (
                  <Button variant={action.variant || 'primary'} onClick={action.onClick}>
                    {action.icon && <span className="mr-2">{action.icon}</span>}
                    {action.label}
                  </Button>
                )}
              </div>
            )}
          </div>
          {subtitle && (
            <p className="truncate text-sm text-neutral-500" title={subtitle}>
              {subtitle}
            </p>
          )}
        </div>
      );
    }

    return (
      // Sous `sm` (telephone) : titre puis actions en dessous, qui passent a
      // la ligne si besoin. A partir de `sm`, seules les classes d'origine
      // s'appliquent (les ajouts sont tous en `max-sm:`), donc rendu
      // identique et un `className` appelant l'emporte comme avant.
      <div
        ref={ref}
        className={cn(
          'flex items-start justify-between gap-4 max-sm:flex-col max-sm:gap-3',
          className
        )}
      >
        <div className="min-w-0 flex-1 max-sm:max-w-full">
          <h1 className="text-2xl font-bold text-dark-500 sm:text-3xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-neutral-500 sm:text-base">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3 max-sm:max-w-full max-sm:flex-wrap">
          {rightContent}
          {action && (
            <Button
              size="lg"
              variant={action.variant || 'primary'}
              onClick={action.onClick}
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </Button>
          )}
        </div>
      </div>
    );
  }
);

PageHeader.displayName = 'PageHeader';

export { PageHeader };
